<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { MAX_FILE_SIZE_BYTES, XLSX_MIME_TYPE } from '@/features/excel-order-upload/constants/file-upload.constants';
import { ButtonVariant } from '@/models/button-variant.enum';
import { Icons } from '@/models/icons.enum';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FileSelection from './FileSelection.vue';
import ModalContainer from './ModalContainer.vue';
import ModalHeader from './ModalHeader.vue';

interface Props {
  isDownloading: boolean;
  isUploading: boolean;
}

const emit = defineEmits<{
  close: [];
  download: [];
  upload: [file: File];
}>();

const props = defineProps<Props>();

const selectedFile = ref<File | null>(null);

const { t } = useI18n();

const updateSelectedFile = (file: File) => {
  selectedFile.value = file;
};

const removeSelectedFile = () => (selectedFile.value = null);

const getDownloadIcon = () => {
  if (props.isDownloading) {
    return Icons.CIRCLE_LOADING;
  } else {
    return Icons.DOWNLOAD_ARROW_SIGN;
  }
};

const getUploadIcon = () => {
  if (props.isUploading) {
    return Icons.CIRCLE_LOADING;
  } else {
    return Icons.UPLOAD;
  }
};
</script>

<template>
  <ModalContainer @close="emit('close')">
    <template #header
      ><ModalHeader
        :title="t('excel_order_upload.excel_upload_modal.title')"
        :description="t('excel_order_upload.excel_upload_modal.description')"></ModalHeader
    ></template>
    <template #main
      ><FileSelection
        :allowed-extensions="['.xlsx']"
        :allowed-mime-types="[XLSX_MIME_TYPE]"
        :max-file-size-bytes="MAX_FILE_SIZE_BYTES"
        @on-change-file="updateSelectedFile"
        @on-remove-file="removeSelectedFile"
    /></template>
    <template #footer>
      <BackstageButton
        :variant="ButtonVariant.SECONDARY"
        :full-width="true"
        @click="emit('download')"
        :icon-right="getDownloadIcon()"
        :disabled="isDownloading || isUploading"
        class="download-button"
        >{{ t('excel_order_upload.excel_upload_modal.button_label.download_template') }}</BackstageButton
      >
      <BackstageButton
        :variant="ButtonVariant.PRIMARY"
        :icon-right="getUploadIcon()"
        :full-width="true"
        :disabled="isUploading || isDownloading || !selectedFile"
        class="upload-button"
        @click="emit('upload', selectedFile!)"
        >{{ t('excel_order_upload.excel_upload_modal.button_label.upload') }}</BackstageButton
      >
    </template>
  </ModalContainer>
</template>

<style lang="scss" scoped>
.download-button:deep(.icon.right) {
  background-color: var(--primary-text-color);
}

.upload-button:deep(.icon.right) {
  background-color: var(--secondary-text-color);
}
</style>
